import { FC, ReactNode, useState } from "react";
import { ThemeObject } from "../ThemeObjects";
import { StyledUniversalDiv } from "./Universal";
import styled from "styled-components";
import { ContentBlockEvents } from "./ContentBlockEvents";
import { createPortal } from "react-dom";
import { PlusIcon } from "lucide-react";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/Dialog";
import { useSaveContentBlock } from "@/Pages/DesignStudio/Design/content-block-management/useSaveContentBlock";
import { ContentBlockShape, ContentBlockType } from "@/models";
import { useGetContentBlockWithChildren } from "@/Pages/DesignStudio/Design/useContentBlockState";
import { Input } from "@headlessui/react";
import { themeToTailwindClasses } from "@/Pages/DesignStudio/Display/content-block-components/polymorphic-block/styled-systed-to-tailwind-converters/styledSystemToTailwindConversions";
import { cn } from "@/util";

type NavbarTheme = Partial<ThemeObject> & {
    bottomBorder: "bordered" | "none" | "drop shadow";
};

type StyledNavbarProps = ContentBlockEvents & {
    theme: Partial<NavbarTheme>;
    children?: ReactNode;
    isEditable?: boolean;
    contentBlockId: string;
    homeLinkText: string;
    homeRoute: string;
    isSidebarOpen: boolean;
};
let dimensions: any;

const StyledComponentNavbar = styled(StyledUniversalDiv)<Partial<NavbarTheme>>`
    width: 100%;
    * {
        color: ${({ color }) => (color ? color : "purple")};
    }
    border-bottom: ${({ bottomBorder }) =>
        bottomBorder === "bordered" ? `1px solid currentcolor` : "none"};
    box-shadow: ${({ bottomBorder }) =>
        bottomBorder === "drop shadow"
            ? `rgb(0 0 0 / 50%) 0px 2px 4px`
            : "none"};
`;

const StyledSideNavWrapper = styled.div`
    > div,
    > button {
        width: 100% !important;
        display: flex !important;
        border-radius: 0.375rem;
        cursor: pointer;
        color: #2563eb !important;
        &:hover {
            background: #2463eb !important;
            color: white !important;
            * {
                color: white !important;
            }
        }
        * {
            font-size: 0.875rem !important;
            border-radius: 0.375rem;
            padding: 0.1rem 0.1rem;
            opacity: 1 !important;
            cursor: pointer;
            color: #2463eb !important;
            &:hover {
                background: #2463eb !important;
                color: white !important;
            }
        }
    }
`;

const buildLink = (
    parentBlock: ContentBlockShape,
    linkText: string,
): ContentBlockShape => {
    const weight = parentBlock.contentBlocks?.reduce((max, child) => {
        return Math.max(max, child.weight);
    }, 0);

    return {
        parent_content_block_id: parentBlock.id,
        content_block_type: ContentBlockType.Link,
        title: linkText,
        round_id: parentBlock.round_id,
        weight,
        rich_text: {
            type: "doc",
            content: [
                {
                    type: "paragraph",
                    content: [
                        {
                            type: "text",
                            text: linkText,
                        },
                    ],
                },
            ],
        },
    };
};

export const StyledNavbar: FC<StyledNavbarProps> = ({
    theme,
    children,
    isEditable,
    contentBlockId,
    homeLinkText,
    onClick,
    onHover,
    homeRoute,
    isSidebarOpen,
    title,
}) => {
    const element = document.querySelector(".navbar") as HTMLElement;
    if (element) dimensions = element.getBoundingClientRect();

    const sideBarNavWrappingElement = document.querySelector(
        "#participant-sim-nav-links",
    );

    const { mutateAsync: saveContentBlock, isPending } = useSaveContentBlock();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [linkText, setLinkText] = useState("");
    const navBlock = useGetContentBlockWithChildren(contentBlockId);

    const classes = themeToTailwindClasses(
        navBlock.theme,
        navBlock.content_block_type,
    );

    return (
        <>
            {sideBarNavWrappingElement &&
                createPortal(
                    <>
                        <div
                            className="navbar navigation space-y-1 border-t py-6"
                            id="side-navigation"
                        >
                            <h3
                                className="px-2 text-sm font-medium text-gray-500"
                                id="simulation-navigation-headline"
                            >
                                {!!children
                                    ? "Simulation Links"
                                    : "No Simulation Links"}
                            </h3>
                            <StyledSideNavWrapper
                                className="space-y-1"
                                role="group"
                                aria-labelledby="simulation-navigation-headline"
                                data-content-block-id={contentBlockId}
                            >
                                {children}
                                {!!isEditable && (
                                    <Dialog
                                        open={isDialogOpen}
                                        onOpenChange={setIsDialogOpen}
                                    >
                                        <DialogTrigger className="!inline-flex items-center justify-start gap-1 rounded-md px-2 py-2 text-white">
                                            <PlusIcon className="h-4 !w-4 max-w-4" />
                                            <span className="!w-auto text-sm">
                                                Add Link
                                            </span>
                                        </DialogTrigger>
                                        <DialogContent className="min-w-[300px] bg-white">
                                            <DialogHeader className="p-4">
                                                <DialogTitle>
                                                    Add Link
                                                </DialogTitle>
                                            </DialogHeader>
                                            <DialogDescription className="flex flex-col gap-2 p-4">
                                                Add a new link to the navigation
                                                bar.
                                                <div className="flex flex-col gap-2">
                                                    <label className="text-sm font-medium">
                                                        Link Text
                                                    </label>
                                                    <Input
                                                        className="rounded-md border-gray-300"
                                                        placeholder="Link Text"
                                                        value={linkText}
                                                        onChange={(e) =>
                                                            setLinkText(
                                                                e.target.value,
                                                            )
                                                        }
                                                    />
                                                </div>
                                            </DialogDescription>
                                            <DialogFooter className="p-4">
                                                <button
                                                    className="rounded-md bg-blue-500 px-4 py-2 text-white disabled:opacity-50"
                                                    onClick={async () => {
                                                        await saveContentBlock(
                                                            buildLink(
                                                                navBlock,
                                                                linkText,
                                                            ),
                                                        );
                                                        setIsDialogOpen(false);
                                                    }}
                                                    disabled={isPending}
                                                >
                                                    {isPending
                                                        ? "Adding..."
                                                        : "Add Link"}
                                                </button>
                                                <DialogClose
                                                    className="rounded-md bg-red-500 px-4 py-2 text-white disabled:opacity-50"
                                                    onClick={() => {
                                                        setIsDialogOpen(false);
                                                    }}
                                                    disabled={isPending}
                                                >
                                                    Cancel
                                                </DialogClose>
                                            </DialogFooter>
                                        </DialogContent>
                                    </Dialog>
                                )}
                            </StyledSideNavWrapper>
                        </div>
                    </>,
                    sideBarNavWrappingElement,
                )}

            <nav className={cn("navbar navigation !hidden @4xl:block", classes)}>
                <div className="flex flex-col gap-4">
                    {navBlock?.contentBlocks?.map((block) => (
                        <div key={block.id}>
                            {children}
                        </div>
                    ))}
                </div>
            </nav>
            {/* <StyledComponentNavbar
                // ref={ref}
                //hardcode classname for animation and padding purposes in other components
                className="navbar navigation hidden @4xl:block"
                position={isEditable ? "relative" : "fixed"}
                //position={"relative"}
                top={0}
                right={0}
                left={0}
                zIndex={90}
                mb={isEditable ? `-${theme.minHeight}` : "10rem"}
                //mb={`-${theme.minHeight}`}
                color={theme.color}
                backgroundColor={theme.backgroundColor}
                data-content-block-id={contentBlockId}
                data-content-block-type={title || "Navbar"}
                onClick={"function" === typeof onClick ? onClick : (evt) => {}}
                onMouseMove={
                    "function" === typeof onHover
                        ? (event: React.MouseEvent) => {
                              onHover(event);
                          }
                        : (evt) => {}
                }
                {...navBarTheme}
            >
                <StyledUniversalDiv
                    display="flex"
                    minHeight={theme.minHeight}
                    ml={32}
                >
                    <StyledUniversalDiv
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        width="100%"
                        py={theme.py}
                        px={theme.px}
                    >
                        <Link href={homeRoute}>
                            {dimensions && theme?.backgroundImage ? (
                                <StyledUniversalDiv
                                    as={"img"}
                                    src={theme.backgroundImage as string}
                                    backgroundImage=""
                                    alignSelf="stretch"
                                    maxWidth={["100%", 100, 150]}
                                    width="200px"
                                />
                            ) : (
                                homeLinkText
                            )}
                        </Link>
                        <StyledUniversalDiv
                            justifyContent="flex-start"
                            gridGap={[5]}
                            display="flex"
                        >
                            {children}
                        </StyledUniversalDiv>
                    </StyledUniversalDiv>
                </StyledUniversalDiv>
            </StyledComponentNavbar> */}
        </>
    );
};

export const defaultNavbarTheme: NavbarTheme = {
    color: "#f4f4f4",
    backgroundColor: "#212121",
    pt: 0,
    pb: 0,
    px: 5,
    minHeight: "3rem", // no form element exists yet
    bottomBorder: "none",
};
